import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  HStack,
  Text,
  useToast,
} from "@chakra-ui/react";

import AppInput from "components/AppInput";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageTaxFrequencyPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch } = useContext(Store);
  const { getTaxFrequency, addTaxFrequency, updateTaxFrequency } =
    useApi();
  const {
    data: taxFrequency,
    isLoading,
    mutate,
  } = useCache("taxFrequency", () => getTaxFrequency(+id || 0));

  const initialValues = useMemo(() => {
    return {
      name: taxFrequency?.name || "",
      days: taxFrequency?.days || "",
      submit: null,
    };
  }, [taxFrequency]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("nom requis"),
    days: Yup.string().trim().required("la fréquence est requis"),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const postData: any = {
          name: values.name,
          days: values.days,
        };
        let response: any = null;
        if (id) {
          response = await updateTaxFrequency({
            id: id,
            ...postData,
          });
        } else {
          response = await addTaxFrequency({
            ...postData,
          });
        }
        if (!response.error_code && !response.errors) {
          dispatch(
            showToast({
              toast: toast,
              message: id
                ? "Période de règlement mise à jour avec succès"
                : "Période de règlement créée avec succès",
              status: toastTypes.success,
            })
          );
          if (id) {
            mutate(getTaxFrequency(+id));
            navigate("/tax-frequencies");
          } else {
            navigate(`/tax-frequencies`);
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minH={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier la période de règlement" : "Créer une période de règlement"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="16px" flexDirection="column">
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                  <HStack>
                      <Text color="gray_description">Nom</Text>
                      <span style={{ color: "red" }}>*</span>
                    </HStack>
                    <FormControl
                      isInvalid={!!errors.name && (touched.name as boolean)}
                    >
                      <AppInput
                        required
                        placeholder={`Nom`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="name"
                        name="name"
                        autoComplete="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        showErrorMessage={Boolean(touched.name && errors.name)}
                        errorMessage={errors.name}
                      />
                    </FormControl>
                  </Flex>
                  <Flex gap="4px" flexDirection="column" alignItems="start">
                    <HStack>
                    <Text color="gray_description">Fréquence</Text>
                      <span style={{ color: "red" }}>*</span>
                    </HStack>
                    <FormControl
                      isInvalid={!!errors.days && (touched.days as boolean)}
                    >
                      <AppInput
                        type="number"
                        required
                        placeholder={`Fréquence`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="days"
                        name="days"
                        autoComplete="days"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.days}
                        showErrorMessage={Boolean(touched.days && errors.days)}
                        errorMessage={errors.days}
                      />
                    </FormControl>
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
